import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5 },
};

const AboutPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>About — Sprout Educational Platform</title>
        <meta name="description" content="Built for South African early childhood education. Learn about Sprout's mission to support CAPS curriculum delivery and child development tracking." />
        <meta property="og:title" content="About — Sprout Educational Platform" />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/about" />
      </Helmet>

      {/* Header */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-display font-bold text-gray-900 dark:text-white"
          >
            About Sprout
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
          >
            We believe every child deserves to have their learning journey documented with care and intention.
          </motion.p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="prose prose-lg mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white">Our Mission</h2>
            </div>

            <div className="space-y-6 text-gray-600 dark:text-gray-300 leading-relaxed">
              <p>
                South Africa's early childhood development (ECD) sector serves over 2 million children,
                yet most centres still rely on paper-based observation and assessment systems. This creates
                gaps in developmental tracking, makes CAPS curriculum alignment difficult, and leaves
                parents disconnected from their children's daily learning.
              </p>
              <p>
                Sprout was built to change that. We provide ECD centres with an intuitive digital platform
                that makes it easy to capture observations, align assessments to the CAPS curriculum,
                generate meaningful reports, and keep parents informed through a secure portal.
              </p>
              <p>
                We're building for the South African context first — with ZAR pricing, POPIA compliance,
                CAPS curriculum alignment, and an understanding of the unique challenges facing ECD
                practitioners across all nine provinces.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fadeInUp} className="text-3xl font-display font-bold text-gray-900 dark:text-white text-center mb-12">
            What we stand for
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Child-Centred',
                description: 'Every feature we build starts with the question: how does this benefit the children?',
                icon: '🌱',
              },
              {
                title: 'Accessible',
                description: 'Affordable pricing and an intuitive interface that works for any ECD practitioner.',
                icon: '🤝',
              },
              {
                title: 'Privacy-First',
                description: 'POPIA-compliant data handling. Children\'s data is sacred and never shared without consent.',
                icon: '🔒',
              },
              {
                title: 'Locally Built',
                description: 'Designed for South Africa with CAPS alignment and ZAR pricing.',
                icon: '🇿🇦',
              },
            ].map((value, i) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="text-center h-full">
                  <div className="text-3xl mb-3">{value.icon}</div>
                  <h3 className="font-display font-semibold text-gray-900 dark:text-white">{value.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{value.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SA ECD Context */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white text-center mb-8">
              The SA ECD landscape
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
              {[
                { stat: '2M+', label: 'Children in ECD programmes' },
                { stat: '42,000+', label: 'ECD centres nationwide' },
                { stat: '9', label: 'Provinces served' },
              ].map(item => (
                <div key={item.label}>
                  <p className="text-4xl font-display font-bold text-primary-600">{item.stat}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{item.label}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
              Sources: Department of Basic Education ECD Census 2021 (children &amp; centres);
              Stats SA general household survey.
            </p>
            <p className="mt-8 text-gray-600 dark:text-gray-300 text-center leading-relaxed">
              With the shift of ECD from the Department of Social Development to Basic Education,
              there's never been a greater need for tools that help centres meet curriculum standards
              while maintaining the warmth and care that defines quality early learning.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-display font-bold text-white">Join us in nurturing South Africa's future</h2>
          <p className="mt-4 text-primary-100">
            Whether you run a small home-based programme or a multi-site ECD group, Sprout is here to help.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/pricing">
              <Button variant="secondary" size="lg" className="bg-white text-primary-700 hover:bg-gray-50">
                Get Started
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="ghost" size="lg" className="text-white border-white/30 border hover:bg-white/10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
